<script setup>
import { computed, ref, watch } from 'vue';
import QuestionBaseQuiz from '@/components/Quiz/QuestionBaseQuiz.vue';
import BadgePill from '@/components/BadgePill.vue';
import PrimeFactorsDisplay from '@/components/Quiz/PrimeFactorsDisplay.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import AppButton from '@/components/AppButton.vue';

const props = defineProps({
    question: {
        required: true,
    },
    modelValue: {
        required: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const number = ref(null);
const answer = ref(props.modelValue);
const factors = ref(props.question.factors);

function reset() {
    answer.value = [];
    //reset factors
    factors.value.forEach((f) => {
        f.qty = 0;
    });
}

const isEmpty = computed(() => {
    return answer.value.length === 0;
});

const stringifiedAnswer = computed(() => {
    if (isEmpty.value) {
        return null;
    } else {
        return JSON.stringify(answer.value);
    }
});

watch(
    () => stringifiedAnswer.value,
    (newVal) => {
        emit('update:modelValue', newVal);
    },
    { immediate: true },
);

const addFactor = (factor) => {
    factor.qty++;
    addNumber(factor.value);
};

const addNumber = (number) => {
    answer.value.push(number);
    answer.value.sort(function (a, b) {
        return a - b;
    });
};

const usedFactors = computed(() => {
    return countAndGroupNumbers(answer.value);
});

function countAndGroupNumbers(numbers) {
    // Create an empty array to store the results
    const result = [];

    // Create a Map to store the count of each number
    const numberCount = new Map();
    for (const number of numbers) {
        const count = numberCount.get(number) || 0;
        numberCount.set(number, count + 1);
    }

    // Convert the Map to an array of objects
    for (const [value, qty] of numberCount.entries()) {
        result.push({ value, qty });
    }

    return result;
}
</script>
<template>
    <QuestionBaseQuiz :question="question">
        <div>
            {{ question.options.number }}
        </div>
        <div v-if="question.options.manual" class="mt-3 flex gap-1 items-center">
            <FormInput
                :id="`answer_${question.question_id}_manual`"
                v-model.number="number"
                :name="`answer_${question.question_id}_manual`"
                @keypress.enter.prevent
                type="number"
                @wheel.passive="$event.target.blur()"
                step="1"
                min="1"
                class="mt-1 w-40"
            />
            <AppButton type="button" disabled v-if="!number">
                {{ $t('messages.add_factor') }}
            </AppButton>
            <AppButton @click="addNumber(number)" type="button" v-else>
                {{ $t('messages.add_factor') }}
            </AppButton>
        </div>

        <div class="flex flex-wrap gap-2 mt-3">
            <button v-for="factor in factors" :key="factor.value" type="button" @click="addFactor(factor)">
                <BadgePill :color="answer.includes(factor.value) ? 'indigo' : 'gray'">
                    {{ factor.value }}
                </BadgePill>
            </button>
        </div>

        <div class="pt-1 text-sm flex flex-wrap gap-2" :class="{ invisible: isEmpty }">
            <div>{{ $t('messages.your_answer') }}:</div>
            <div class="px-2 py-1 bg-gray-100 tabular-nums">
                <PrimeFactorsDisplay :factors="usedFactors" />
            </div>
        </div>

        <div class="mt-1">
            <button @click="reset" :class="{ invisible: isEmpty }" class="text-gray-600" type="button">
                <svg
                    class="w-6 h-6 inline-block"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
                {{ $t('messages.delete_answer') }}
            </button>
        </div>
    </QuestionBaseQuiz>
</template>
